/* eslint-disable no-undef */
import { Loader } from '@googlemaps/js-api-loader'
import MarkerClusterer from '@googlemaps/markerclustererplus'
import { get, isNumber, reduce, set, some, toNumber } from 'lodash'

import { getInfoWindowLayout as placeInfoWindow } from './infowindow-place'
import { getInfoWindowLayout as activityInfoWindow } from './infowindow-activity'
import { fetchItemsFromMainCollections } from '../helpers/api'
import { getPosition, getRequestParamsFromURLParams, resetRequestParams, lockBodyScrolling } from '../helpers/helpers'
import { COLLECTIONS } from '../helpers/constants'

export default class GMaps {
  constructor (options) {
    this.gMap = null
    this.gInfoWindow = null
    this.gMarkerCluster = null
    this.coords = {
      latitude: undefined,
      longitude: undefined
    }
    this.zoom = null
    this.collection = options?.collection || COLLECTIONS.PLACES
    this.items = null
    this.itemsMarkers = []
    this.shown = false
    this.requestParams = {
      status: 'published',
      has_location: true,
      page_size: 500,
      page: 1,
      order_by: 'clients'
    }
  }

  static get DEFAULT_ZOOM () {
    return 15
  }

  static get DEFAULT_COORDS () {
    return {
      LAT: 40.4159547,
      LNG: -3.7072532
    }
  }

  static get MOVE_TO () {
    return {
      COORDS: 'COORDS',
      CENTER: 'CENTER',
      MARKERS: 'MARKERS',
      DEFAULT: 'DEFAULT'
    }
  }

  static get EVENT () {
    return {
      LOADING: 'poin:gmaps:loading',
      LOADED: 'poin:gmaps:loaded',
      BOUNDS_CHANGE: 'poin:gmaps:bounds_change',
      ITEMS_ADDED: 'poin:gmaps:ITEMS_ADDED',
      LOCATION_CHANGE: 'poin:gmaps:location_change'
    }
  }

  static get ANIMATION_DELAY () { // match places.scss:73
    return 350
  }

  static removeListener (listener) {
    if (listener) {
      google.maps.event.removeListener(listener)
    }
  }

  async init () {
    const queryParams = new URLSearchParams(window.location.search)
    const latitude = queryParams.get('latitude')
    const longitude = queryParams.get('longitude')

    if (latitude && longitude) {
      this.coords.latitude = toNumber(latitude)
      this.coords.longitude = toNumber(longitude)
    }

    const loader = new Loader({
      apiKey: window.googleApiKey,
      version: 'weekly'
    })

    await loader.load()
    this.mapWrapper = document.getElementById('places-map') // TODO : NOMBRE MÁS GENÉRICO
    this.mapContainer = document.getElementById('map')
    this.gMap = new google.maps.Map(this.mapContainer, {
      center: {
        lat: toNumber(this.coords.latitude) || GMaps.DEFAULT_COORDS.LAT,
        lng: toNumber(this.coords.longitude) || GMaps.DEFAULT_COORDS.LNG
      },
      zoom: GMaps.DEFAULT_ZOOM,
      mapTypeControl: false,
      fullscreenControl: false,
      gestureHandling: 'greedy',
      streetViewControl: false,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [
            { visibility: 'off' }
          ]
        }
      ]
    })

    const initialLoadListener = this.gMap.addListener('bounds_changed', () => {
      this.mapContainer.dispatchEvent(new CustomEvent(GMaps.EVENT.BOUNDS_CHANGE, {
        bubbles: true,
        detail: this.gMap.getBounds().toJSON()
      }))
      GMaps.removeListener(initialLoadListener)
    })
    this.gInfoWindow = new google.maps.InfoWindow({ maxWidth: 320 })
    this._handleMapButtons()
  }

  async addNewItems (newItems, append) {
    if (append) {
      this.items.count = newItems.count
      this.items.next = newItems.next
      this.items.previous = null
      this.items.results = this.items.results.concat(newItems.results)
    } else {
      this.items = newItems
    }

    return this.displayItemsOnMap()
  }

  displayItemsOnMap () {
    this.removeAllMarkers()

    if (this.items?.results.length) {
      this.itemsMarkers = this.items.results.map((item) => this._createMarker(item))
      const markersToCluster = this.itemsMarkers
        .filter((markerObject) => markerObject.marker?.category === 'playground')
        .map((markerObject) => markerObject.marker)
      this.gMarkerCluster = new MarkerClusterer(this.gMap, markersToCluster, {
        imagePath: '/static/poin_app/img/gmaps/m',
        maxZoom: GMaps.DEFAULT_ZOOM
      })
    }
    return this.itemsMarkers.length || 0
  }

  removeAllMarkers () {
    if (this.gMarkerCluster) this.gMarkerCluster.setMap(null)
    this.itemsMarkers.forEach((markerObject) => {
      GMaps.removeListener(markerObject.listener)
      markerObject.marker?.setMap(null)
    })
    this.itemsMarkers = []
  }

  move (type, coords = {}) {
    if (!type || !Object.values(GMaps.MOVE_TO).includes(type)) {
      throw new Error(`${type} is not a valid GMaps.MOVE_TO`)
    }

    switch (type) {
      case GMaps.MOVE_TO.COORDS:
        if (!isNumber(coords.lat) && !isNumber(coords.lng)) {
          throw new Error(`${type} needs second object argument {lat: number, lng: number}, received ${coords}`)
        }
        this.gMap.setCenter(coords)
        if (!this.zoom) this.gMap.setZoom(GMaps.DEFAULT_ZOOM)
        break
      case GMaps.MOVE_TO.MARKERS: {
        const bounds = new google.maps.LatLngBounds()

        this.itemsMarkers.forEach((markerObject, i) => {
          if (!markerObject.marker) return

          bounds.extend(markerObject.marker.getPosition())
        })

        this.gMap.fitBounds(bounds)

        if (this.gMap.getZoom() > GMaps.DEFAULT_ZOOM) {
          this.gMap.setZoom(GMaps.DEFAULT_ZOOM)
        }
      }
        break
      case GMaps.MOVE_TO.CENTER:
        this.gMap.setCenter({
          lat: this.coords.latitude,
          lng: this.coords.longitude
        })
        this.gMap.setZoom(GMaps.DEFAULT_ZOOM)
        break
      case GMaps.MOVE_TO.DEFAULT:
      default:
        this.gMap.setCenter({
          lat: GMaps.DEFAULT_COORDS.LAT,
          lng: GMaps.DEFAULT_COORDS.LNG
        })
        this.gMap.setZoom(GMaps.DEFAULT_ZOOM)
        break
    }
  }

  _updateSearchWithMapPosition () {
    this.coords.latitude = this.gMap.getCenter().lat()
    this.coords.longitude = this.gMap.getCenter().lng()
    this.zoom = this.gMap.getZoom()
    this.mapWrapper.dispatchEvent(new CustomEvent(GMaps.EVENT.LOCATION_CHANGE, {
      bubbles: true,
      detail: {
        latitude: this.coords.latitude,
        longitude: this.coords.longitude,
        apply: false
      }
    }))
  }

  async _centerMapOnUserPosition () {
    const position = await getPosition({ enableHighAccuracy: true })
    this.coords.latitude = position.coords.latitude
    this.coords.longitude = position.coords.longitude
    this.move(GMaps.MOVE_TO.CENTER)
    this.mapWrapper.dispatchEvent(new CustomEvent(GMaps.EVENT.LOCATION_CHANGE, {
      bubbles: true,
      detail: {
        latitude: this.coords.latitude,
        longitude: this.coords.longitude,
        apply: true
      }
    }))
  }

  /**
   * @param {boolean} hasLocation Prevents centering map if location is stablished
   * @param {boolean} append Append to existing markers instead of replacing
   */
  async populate (hasLocation = false, append = false) {
    this.addLoader()
    await this._prepareMap()
    resetRequestParams(this.collection, this.requestParams)

    const _params = reduce(await getRequestParamsFromURLParams(this.collection, this.gMap), (acc, value, key) => {
      if (value) { set(acc, key, value) }
      return acc
    }, this.requestParams)

    const items = await fetchItemsFromMainCollections(this.collection, _params)
    await this.addNewItems(items, append)

    if (!hasLocation) {
      if (some(items.results, (item) => get(item, 'geolocation'))) {
        this.move(GMaps.MOVE_TO.MARKERS)
      } else {
        this.move(GMaps.MOVE_TO.DEFAULT)
      }
    }

    this.zoomListener = this.gMap.addListener('zoom_changed', async () => {
      this.zoomIdleListener ||= this.gMap.addListener('idle', async () => {
        await this._interactivePopulate()
        GMaps.removeListener(this.zoomIdleListener)
        this.zoomIdleListener = undefined
      })
    })
    this.dragListener = this.gMap.addListener('dragend', async () => {
      this.dragIdleListener ||= this.gMap.addListener('idle', async () => {
        await this._interactivePopulate()
        this._updateSearchWithMapPosition()
        GMaps.removeListener(this.dragIdleListener)
        this.dragIdleListener = undefined
      })
    })

    await this._restoreMap()
    this.removeLoader()
  }

  async _interactivePopulate () {
    this.addLoader()
    resetRequestParams(this.collection, this.requestParams)

    const _params = reduce(await getRequestParamsFromURLParams(this.collection, this.gMap, { addMapBounds: true }), (acc, value, key) => {
      if (value) { set(acc, key, value) }
      return acc
    }, this.requestParams)
    const items = await fetchItemsFromMainCollections(this.collection, _params)
    await this.addNewItems(items)
    this.removeLoader()

    this.mapWrapper.dispatchEvent(new CustomEvent(GMaps.EVENT.ITEMS_ADDED, {
      bubbles: true,
      detail: this.items
    }))
  }

  _createMarker (item) {
    let marker
    let listener

    if (item.geolocation) {
      const geolocation = JSON.parse(item.geolocation)
      const pos = {
        lat: geolocation.coordinates[1],
        lng: geolocation.coordinates[0]
      }
      marker = new google.maps.Marker({
        position: pos,
        map: this.gMap,
        title: item.name,
        icon: item.rating_category?.marker // TODO: VER SI GOOGLE ACEPTA UN ICON UNDEFINED
      })

      marker.category = item.rating_category?.code_name || ''
      listener = marker.addListener('click', () => {
        const iwContent = this.collection === COLLECTIONS.PLACES ? placeInfoWindow(item) : activityInfoWindow(item)
        this.gInfoWindow.setContent(iwContent)
        this.gInfoWindow.open(this.gMap, marker)
      })
    }

    return {
      marker,
      listener
    }
  }

  _handleMapButtons () {
    const locationButton = document.createElement('button')
    locationButton.classList.add('custom-map-control-button', 'mdc-button', 'mdc-button--outlined', 'custom-contained-button')
    locationButton.textContent = 'Centrar el mapa'
    this.gMap.controls[google.maps.ControlPosition.TOP_CENTER].push(locationButton)
    locationButton.addEventListener('click', () => this._centerMapOnUserPosition())

    if (this.isFullScreenMap()) {
      this.mapWrapper.classList.add('full-screen-map')
      document.getElementById('close-map').addEventListener('click', () => { this.hide() })
    }
  }

  async _prepareMap () {
    if (this.isFullScreenMap() && !this.shown) {
      return new Promise((resolve) => {
        this.mapWrapper.style.visibility = 'hidden'
        setTimeout(() => {
          this.mapWrapper.style.zIndex = 1
          resolve()
        }, GMaps.ANIMATION_DELAY)
      })
    } else {
      return Promise.resolve()
    }
  }

  async _restoreMap () {
    if (this.isFullScreenMap() && !this.shown) {
      return new Promise((resolve) => {
        this.mapWrapper.style.zIndex = -1
        setTimeout(() => {
          this.mapWrapper.style.visibility = 'visible'
          resolve()
        }, GMaps.ANIMATION_DELAY)
      })
    } else {
      return Promise.resolve()
    }
  }

  /**
   * @param {{
   *   page_size: number,
   *   page: number
   * }}
   */
  changePage ({ page_size, page }) { // eslint-disable-line camelcase
    let changed = false

    if (page_size * page >= this.requestParams.page_size * this.requestParams.page) { // eslint-disable-line camelcase
      changed = true
      this.requestParams.page += 1
    }

    return changed
  }

  addLoader () {
    if (this.isFullScreenMap()) {
      this.mapWrapper.dispatchEvent(new Event(GMaps.EVENT.LOADING, { bubbles: true }))
    } else {
      this.mapContainer.insertAdjacentHTML('afterbegin', '<div class="preloader-backdrop"><div class="preloader"></div></div>')
    }
  }

  removeLoader () {
    if (this.isFullScreenMap()) {
      this.mapWrapper.dispatchEvent(new Event(GMaps.EVENT.LOADED, { bubbles: true }))
    } else {
      this.mapContainer.getElementsByClassName('preloader-backdrop')[0].remove()
    }
  }

  show () {
    this.shown = true
    lockBodyScrolling(true)
    this.mapWrapper.classList.add('show')

    setTimeout(() => {
      this.itemsMarkers.forEach((markerObject) => { markerObject.marker?.setVisible(true) })
      this.gMarkerCluster.repaint()
    }, GMaps.ANIMATION_DELAY)
  }

  hide () {
    this.shown = false
    lockBodyScrolling(false)
    this.gInfoWindow.close()
    this.itemsMarkers.forEach((markerObject) => { markerObject.marker?.setVisible(false) })
    this.gMarkerCluster.repaint()
    this.mapWrapper.classList.remove('show')
  }

  isFullScreenMap () {
    return document.documentElement.clientWidth < 1200
  }
}
